import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Radio, Send, Headphones, Image as ImageIcon, Hand, PhoneCall, PhoneOff, Download, Music2, ListMusic, Share2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { createListener, createCaller } from "@/lib/webrtc";
import FloatingReactions, { REACTIONS } from "@/components/FloatingReactions";
import ChatAvatar from "@/components/ChatAvatar";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import { nextLive, formatNextLive } from "@/lib/host";
import Countdown from "@/components/Countdown";

const HOST_IMG = "https://customer-assets-rejwkqb3.emergentagent.net/job_praise-fm-1/artifacts/7oymz8dy_ai-generated-photo.png";

export default function LiveShow() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [tuned, setTuned] = useState(false);
  const [episodes, setEpisodes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [callState, setCallState] = useState({ status: "none" });
  const [selfMuted, setSelfMuted] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const onAirRef = useRef(null);
  const audioRef = useRef(null);
  const listenerRef = useRef(null);
  const callerRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    const poll = () => api.get("/live/status").then((r) => setStatus(r.data)).catch(() => {});
    poll();
    const t = setInterval(poll, 8000);
    api.get("/episodes").then((r) => setEpisodes(r.data)).catch(() => {});
    return () => { clearInterval(t); if (listenerRef.current) listenerRef.current.stop(); if (callerRef.current) callerRef.current.stop(); };
  }, []);

  // Presence ping + call-in state polling while tuned in
  useEffect(() => {
    if (!tuned || !user || !status?.id) return;
    const sid = status.id;
    const ping = () => {
      api.post(`/live/${sid}/presence`).catch(() => {});
      api.post("/listening/heartbeat", { kind: "live" }).catch(() => {});
      api.get(`/live/${sid}/callin/mine`).then((r) => setCallState(r.data)).catch(() => {});
    };
    ping();
    const t = setInterval(ping, 5000);
    return () => clearInterval(t);
  }, [tuned, user, status?.id]);

  // React to call state changes (host ended / muted)
  useEffect(() => {
    if (callState.status === "ended" && callerRef.current) {
      callerRef.current.stop(); callerRef.current = null;
      if (audioRef.current) audioRef.current.muted = false;
      setSelfMuted(false); onAirRef.current = null;
      toast.message("Your call has ended");
    }
  }, [callState.status]);

  // On-air timer
  useEffect(() => {
    if (callState.status !== "live") { setElapsed(0); return; }
    if (!onAirRef.current) onAirRef.current = Date.now();
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - onAirRef.current) / 1000)), 1000);
    return () => clearInterval(t);
  }, [callState.status]);

  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const nextShow = nextLive();

  useEffect(() => {
    if (!tuned) return;
    const loadChat = () => api.get("/chat?room=live").then((r) => setMessages(r.data)).catch(() => {});
    loadChat();
    const t = setInterval(loadChat, 4000);
    return () => clearInterval(t);
  }, [tuned]);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

  const tuneIn = () => {
    if (!status?.id) return;
    listenerRef.current = createListener(status.id, audioRef.current);
    setTuned(true);
  };

  const react = (emoji) => {
    if (!user) { navigate("/login"); return; }
    if (!status?.id) return;
    api.post(`/live/${status.id}/react`, { emoji }).catch(() => {});
  };

  const raiseHand = async () => {
    if (!user) { navigate("/login"); return; }
    if (!status?.id) return;
    try { const { data } = await api.post(`/live/${status.id}/callin/request`); setCallState(data); toast.success("Hand raised — the host will invite you shortly 🙌"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not raise hand"); }
  };

  const joinCall = async () => {
    if (!status?.id) return;
    let mic;
    try { mic = await navigator.mediaDevices.getUserMedia({ audio: true }); }
    catch (e) { toast.error(e.name === "NotAllowedError" ? "Microphone permission denied" : "Could not access your mic"); return; }
    // 3-2-1 countdown cue
    await new Promise((resolve) => {
      let n = 3; setCountdown(n);
      const iv = setInterval(() => {
        n -= 1;
        if (n <= 0) { clearInterval(iv); setCountdown(0); setTimeout(() => { setCountdown(null); resolve(); }, 600); }
        else setCountdown(n);
      }, 900);
    });
    if (audioRef.current) audioRef.current.muted = true; // prevent echo
    onAirRef.current = Date.now();
    callerRef.current = createCaller(status.id, user.id, mic, () => {});
    await api.post(`/live/${status.id}/callin/mine/live`).catch(() => {});
    setCallState((s) => ({ ...s, status: "live" }));
    toast.success("You're on air! 🔴 Playback muted to prevent echo — use headphones if needed.");
  };

  const leaveCall = async () => {
    if (callerRef.current) { callerRef.current.stop(); callerRef.current = null; }
    if (audioRef.current) audioRef.current.muted = false;
    setSelfMuted(false); onAirRef.current = null;
    if (status?.id && callState.id) await api.post(`/live/${status.id}/callin/${callState.id}/end`).catch(() => {});
    setCallState({ status: "none" });
    toast.message("You left the call");
  };

  const toggleSelfMute = () => {
    const m = !selfMuted;
    setSelfMuted(m);
    if (callerRef.current) callerRef.current.setMuted(m);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (!user) { navigate("/login"); return; }
    const t = text; setText("");
    try { const { data } = await api.post("/chat", { text: t, room: "live" }); setMessages((p) => [...p, data]); } catch (e) {}
  };

  const active = status?.active;

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-10">
      {countdown !== null && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#050818]/85 backdrop-blur-sm" data-testid="callin-countdown">
          <div className="text-center">
            <p className="overline text-[#E6B34A] mb-4">Get ready — you're going on air</p>
            <div key={countdown} className="font-display font-black text-white leading-none animate-in zoom-in-50 duration-300" style={{ fontSize: "10rem" }}>
              {countdown === 0 ? "LIVE" : countdown}
            </div>
          </div>
        </div>
      )}
      <audio ref={audioRef} className="hidden" />
      <div className="flex items-center gap-3 mb-1">
        <Mic className="w-5 h-5 text-[#E6B34A]" />
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">The Gospel Show</h1>
      </div>
      <p className="text-zinc-400 mb-8">Live host broadcasts and on-demand episodes.</p>

      {!active && nextShow && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5 rounded-2xl border border-[#E6B34A]/20 bg-[#141C42] px-5 py-4 mb-8" data-testid="schedule-banner">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-full bg-[#E6B34A]/10 flex items-center justify-center shrink-0">
              <Radio className="w-5 h-5 text-[#E6B34A]" />
            </div>
            <div className="min-w-0">
              <p className="overline text-[#E6B34A]">David is live next</p>
              <p className="font-semibold text-sm mt-0.5" data-testid="schedule-next"><span className="text-white">{nextShow.title}</span> · {formatNextLive(nextShow)}</p>
            </div>
          </div>
          <div className="rounded-xl bg-[#0C1338] border border-white/5 px-4 py-2.5 shrink-0">
            <Countdown target={nextShow.date} testid="live-countdown" />
          </div>
          <Link to="/meet-the-host" data-testid="schedule-view-host" className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 text-sm font-semibold transition-colors shrink-0">
            View host profile
          </Link>
        </div>
      )}

      {active ? (
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <div className="relative rounded-2xl border border-[#E6B34A]/30 bg-[#141C42] p-8 overflow-hidden" data-testid="live-card">
              <FloatingReactions sid={status.id} />
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#E6B34A]/10 blur-3xl" />
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF3B30]/90 text-white text-xs font-bold"><span className="w-1.5 h-1.5 rounded-full bg-white live-dot" /> LIVE NOW</span>
              <h2 className="font-display font-black text-3xl tracking-tighter mt-4">{status.title}</h2>
              <p className="text-zinc-400 mt-1">Hosted by {status.host_name}</p>
              {status.now_playing?.title && (
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E6B34A]/10 border border-[#E6B34A]/30 max-w-full relative z-10" data-testid="live-now-playing">
                  <Music2 className="w-3.5 h-3.5 text-[#E6B34A] shrink-0" />
                  <span className="overline text-[#E6B34A] shrink-0">{status.now_playing.kind === "sermon" ? "Now preaching" : "Now playing"}</span>
                  <span className="text-sm font-semibold truncate">{status.now_playing.title}{status.now_playing.artist ? ` — ${status.now_playing.artist}` : ""}</span>
                </div>
              )}
              {status.recent_tracks?.length > 0 && (
                <div className="mt-4 relative z-10" data-testid="live-recent-tracks">
                  <p className="overline text-zinc-500 mb-2 flex items-center gap-1.5"><ListMusic className="w-3.5 h-3.5" /> Recently on air</p>
                  <div className="space-y-1">
                    {status.recent_tracks.slice(0, 5).map((t, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-zinc-400" data-testid={`recent-track-${i}`}>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${t.kind === "sermon" ? "bg-blue-500/15 text-blue-300" : "bg-[#E6B34A]/15 text-[#E6B34A]"}`}>{t.kind === "sermon" ? "Sermon" : "Song"}</span>
                        <span className="truncate">{t.title}{t.artist ? ` — ${t.artist}` : ""}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!tuned ? (
                <button onClick={tuneIn} data-testid="tune-in" className="mt-6 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors relative z-10">
                  <Headphones className="w-5 h-5" /> Tune In
                </button>
              ) : (
                <p className="mt-6 flex items-center gap-2 text-emerald-400 font-semibold relative z-10" data-testid="tuned-in"><span className="w-2 h-2 rounded-full bg-emerald-400 live-dot" /> Connected — you're listening live</p>
              )}
              <div className="mt-6 flex flex-wrap gap-2 relative z-10" data-testid="reaction-bar">
                {REACTIONS.map((emo) => (
                  <button key={emo} onClick={() => react(emo)} data-testid={`react-${emo}`} className="w-11 h-11 rounded-full bg-white/5 hover:bg-white/15 text-2xl transition-transform hover:scale-110 active:scale-95">{emo}</button>
                ))}
              </div>

              {tuned && (
                <div className="mt-6 relative z-10" data-testid="callin-controls">
                  {callState.status === "none" && (
                    <button onClick={raiseHand} data-testid="raise-hand" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/5 hover:bg-white/10 font-semibold transition-colors">
                      <Hand className="w-4 h-4 text-[#E6B34A]" /> Request to call in
                    </button>
                  )}
                  {callState.status === "requested" && (
                    <div className="inline-flex items-center gap-3 px-5 py-3 rounded-full bg-[#E6B34A]/10 border border-[#E6B34A]/30" data-testid="callin-waiting">
                      <Hand className="w-4 h-4 text-[#E6B34A]" /> <span className="text-sm font-semibold text-[#E6B34A]">Hand raised — waiting for the host…</span>
                      <button onClick={leaveCall} data-testid="cancel-hand" className="text-zinc-400 hover:text-white text-sm underline">Cancel</button>
                    </div>
                  )}
                  {callState.status === "granted" && (
                    <button onClick={joinCall} data-testid="join-call" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 text-white font-bold hover:bg-emerald-600 transition-colors animate-pulse">
                      <PhoneCall className="w-4 h-4" /> {callState.initiated_by === "host" ? "The host invited you — Join the call" : "You're up! Join the call"}
                    </button>
                  )}
                  {callState.status === "live" && (
                    <div className="flex items-center gap-3 flex-wrap" data-testid="callin-onair-self">
                      <span className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#FF3B30]/15 border border-[#FF3B30]/40 text-[#FF3B30] font-bold text-sm">
                        <span className="w-2 h-2 rounded-full bg-[#FF3B30] live-dot" /> {callState.muted ? "Muted by host" : "You're on air"}
                        <span className="tabular-nums ml-1 text-white/90" data-testid="onair-timer">{fmtTime(elapsed)}</span>
                      </span>
                      <button onClick={toggleSelfMute} data-testid="self-mute" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 hover:bg-white/10 font-semibold text-sm transition-colors">
                        {selfMuted ? <><MicOff className="w-4 h-4 text-[#FF3B30]" /> Unmute mic</> : <><Mic className="w-4 h-4 text-emerald-400" /> Mute mic</>}
                      </button>
                      <button onClick={leaveCall} data-testid="leave-call" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 hover:bg-[#FF3B30]/15 text-[#FF3B30] font-semibold text-sm transition-colors">
                        <PhoneOff className="w-4 h-4" /> Leave call
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="lg:col-span-2 flex flex-col rounded-2xl border border-white/5 bg-[#0C1338] p-5 h-[460px]">
            <p className="overline text-[#E6B34A] mb-3">Live show chat</p>
            <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-3" data-testid="live-chat">
              {messages.length === 0 ? <p className="text-zinc-600 text-sm text-center py-8">Say hello to the host 👋</p> :
                messages.map((m) => (
                  <div key={m.id} className="flex items-start gap-2">
                    <ChatAvatar userId={m.user_id} name={m.user_name} size={24} />
                    <p className="text-sm min-w-0"><span className={`font-bold ${m.role === "admin" || m.role === "host" ? "text-[#E6B34A]" : "text-zinc-300"}`}>{m.user_name}</span> <span className="text-zinc-400 break-words">{m.text}</span></p>
                  </div>
                ))}
            </div>
            <form onSubmit={send} className="flex gap-2 mt-3">
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Message…" data-testid="live-chat-input" className="flex-1 rounded-xl bg-[#1E2A5A] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]" />
              <button type="submit" data-testid="live-chat-send" aria-label="Send" className="w-10 h-10 rounded-xl bg-[#E6B34A] text-[#0A1030] flex items-center justify-center"><Send className="w-4 h-4" /></button>
            </form>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center mb-10" data-testid="no-live">
          <p className="text-zinc-400">No live broadcast right now. Catch up with episodes below.</p>
        </div>
      )}

      {/* Your Host */}
      <section className="mt-12 relative overflow-hidden rounded-3xl border border-[#E6B34A]/20 bg-gradient-to-br from-[#161F47] via-[#141C42] to-[#0C1338]" data-testid="host-feature">
        <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-[#E6B34A]/10 blur-3xl" />
        <div className="absolute -bottom-24 right-10 w-80 h-80 rounded-full bg-[#5B7CFA]/10 blur-3xl" />
        <div className="relative grid md:grid-cols-2 gap-0 items-end">
          <div className="flex justify-center md:justify-start items-end pt-8 md:pt-0 px-6 md:pl-10">
            <img
              src={HOST_IMG}
              alt="David Porter — Host"
              data-testid="host-photo"
              className="w-auto h-[300px] md:h-[380px] object-contain object-bottom drop-shadow-[0_20px_40px_rgba(0,0,0,0.5)]"
            />
          </div>
          <div className="p-8 md:p-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-5">
              <Mic className="w-3.5 h-3.5 text-[#E6B34A]" />
              <span className="overline text-[#E6B34A]">Your Host</span>
            </div>
            <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-none">David Porter</h2>
            <p className="text-[#E6B34A] font-semibold mt-3">Host · The Gospel Show</p>
            <p className="text-zinc-300 leading-relaxed mt-5 max-w-lg">
              With a heart for worship and a voice that feels like home, David Porter brings you spirit-filled
              conversation, uplifting gospel and honest prayer every broadcast. Pull up a seat, tune in, and
              let's lift up the name of Jesus together.
            </p>
            <div className="flex flex-wrap gap-2 mt-6" data-testid="host-tags">
              {["Praise & Worship", "Prayer & Encouragement", "Live Call-ins"].map((t) => (
                <span key={t} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-[#E6B34A]/10 text-[#E6B34A]">{t}</span>
              ))}
            </div>
            {active && !tuned && (
              <button onClick={tuneIn} data-testid="host-tune-in" className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors">
                <Headphones className="w-4 h-4" /> Tune in to David now
              </button>
            )}
            <div className="mt-7">
              <Link to="/meet-the-host" data-testid="host-view-profile" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/5 hover:bg-white/10 font-bold transition-colors">
                View full profile & schedule →
              </Link>
            </div>
          </div>
        </div>
      </section>

      <h2 className="font-display font-extrabold text-xl lg:text-2xl tracking-tight mt-12 mb-5 flex items-center gap-2"><Radio className="w-5 h-5 text-[#E6B34A]" /> Episodes</h2>
      <div className="grid md:grid-cols-2 gap-5" data-testid="episodes-list">
        {episodes.length === 0 ? <p className="text-zinc-500">No episodes published yet.</p> :
          episodes.map((ep) => (
            <div key={ep.id} className="rounded-2xl border border-white/5 bg-[#141C42] p-5 flex gap-4" data-testid={`episode-${ep.id}`}>
              <div className="w-20 h-20 rounded-xl overflow-hidden bg-[#0C1338] border border-white/10 flex items-center justify-center shrink-0">
                {ep.has_cover ? <img src={`${api.defaults.baseURL}${ep.cover_url}`} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} /> : null}
                <div className="w-full h-full items-center justify-center" style={{ display: ep.has_cover ? "none" : "flex" }}><ImageIcon className="w-6 h-6 text-zinc-600" /></div>
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-display font-bold text-lg tracking-tight truncate">{ep.title}</h3>
                <p className="text-xs text-zinc-500 mt-0.5">by {ep.host_id ? <Link to={`/hosts/${ep.host_id}`} className="text-[#E6B34A] hover:underline" data-testid={`host-link-${ep.id}`}>{ep.host_name}</Link> : ep.host_name}</p>
                {ep.description && <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{ep.description}</p>}
                <audio id={`ep-audio-${ep.id}`} controls className="w-full mt-2 h-9"><source src={`${api.defaults.baseURL}/episodes/${ep.id}/audio`} /></audio>
                {ep.tracks?.length > 0 && (
                  <details className="mt-2 group" data-testid={`episode-tracks-${ep.id}`}>
                    <summary className="text-xs font-semibold text-zinc-400 hover:text-white cursor-pointer inline-flex items-center gap-1.5 list-none"><ListMusic className="w-3.5 h-3.5 text-[#E6B34A]" /> {ep.tracks.length} chapter{ep.tracks.length === 1 ? "" : "s"} — tap to jump</summary>
                    <div className="mt-2 space-y-1 pl-1">
                      {ep.tracks.map((t, i) => (
                        <button key={i} onClick={() => { const a = document.getElementById(`ep-audio-${ep.id}`); if (a) { a.currentTime = t.at || 0; a.play().catch(() => {}); } }} data-testid={`chapter-${ep.id}-${i}`} className="w-full flex items-center gap-2 text-xs text-zinc-400 hover:text-white text-left rounded-lg px-1.5 py-1 hover:bg-white/5 transition-colors">
                          <span className="font-mono text-[#E6B34A] tabular-nums shrink-0">{`${String(Math.floor((t.at || 0) / 60)).padStart(2, "0")}:${String(Math.floor((t.at || 0) % 60)).padStart(2, "0")}`}</span>
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${t.kind === "sermon" ? "bg-blue-500/15 text-blue-300" : "bg-[#E6B34A]/15 text-[#E6B34A]"}`}>{t.kind === "sermon" ? "Sermon" : "Song"}</span>
                          <span className="truncate">{t.title}{t.artist ? ` — ${t.artist}` : ""}</span>
                        </button>
                      ))}
                    </div>
                  </details>
                )}
                <a href={`${api.defaults.baseURL}/episodes/${ep.id}/audio?download=1`} download data-testid={`download-episode-${ep.id}`} className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-[#E6B34A] hover:underline">
                  <Download className="w-3.5 h-3.5" /> Download
                </a>
                <button onClick={async () => { const url = `${api.defaults.baseURL}/episodes/${ep.id}/share`; const text = `🎙️ Listen to "${ep.title}" on In His Image Radio`; try { if (navigator.share) { await navigator.share({ title: ep.title, text, url }); } else { await navigator.clipboard.writeText(`${text} ${url}`); toast.success("Link copied — share it!"); } } catch (e) {} }} data-testid={`share-episode-${ep.id}`} className="inline-flex items-center gap-1.5 mt-2 ml-4 text-xs font-semibold text-[#E6B34A] hover:underline">
                  <Share2 className="w-3.5 h-3.5" /> Share
                </button>
                <Link to={`/episodes/${ep.id}`} data-testid={`open-episode-${ep.id}`} className="inline-flex items-center gap-1.5 mt-2 ml-4 text-xs font-semibold text-zinc-400 hover:text-white">Open page →</Link>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
